import React, {
  useState,
  useMemo,
  useEffect,
  useRef,
  useCallback,
} from "react";
import { Alert, Box, CircularProgress } from "@mui/material";
import {
  X,
  RefreshCw,
  Search,
  GripVertical,
  Pin,
  PinOff,
  Eye,
  EyeOff,
  Check,
} from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { MAIN_URL, getApiHeaders } from "../../Configurations/Urls";
import { buildDatagridKey } from "../../Configurations/datagridKey";
import { mergeColumnLayouts } from "../../utils/buildBackendListColumns";
import { resolvePersonalizeCatalogColumns } from "../../utils/personalizeCatalog";
import { FilterBuilder } from "../filters";
import useAuthStore from "../../Zustand/Store/useAuthStore";

function formatPreviewCellValue(value) {
  if (value === null || value === undefined) return "";
  if (typeof value === "object") {
    if (React.isValidElement(value)) return "";
    try {
      return JSON.stringify(value);
    } catch {
      return "";
    }
  }
  return String(value);
}

/** Extracts a row array from common API response shapes (wrapper keys, paginators, direct array). */
function extractDataArrayFromResponse(responseData) {
  if (responseData == null || typeof responseData !== "object") return [];
  if (Array.isArray(responseData)) return responseData;
  if (Array.isArray(responseData.data)) return responseData.data;
  for (const value of Object.values(responseData)) {
    if (Array.isArray(value)) return value;
    if (value && typeof value === "object" && Array.isArray(value.data)) return value.data;
  }
  return [];
}

function estimateColumnWidth(label, minWidth = 120, maxWidth = 320) {
  const text = String(label || "").trim();
  const estimated = text.length * 9 + 48;
  return Math.min(maxWidth, Math.max(minWidth, estimated));
}

function withReadableColumnWidth(column) {
  if (!column) return column;
  const width = Math.max(
    Number(column.width) || 0,
    estimateColumnWidth(column.label || column.key)
  );
  return { ...column, width };
}


/** Convert snake_case / kebab-case table keys into a readable title. */
function humanizeTableName(name) {
  if (!name || typeof name !== "string") return "";
  return name
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

const PersonalizeDataGrid = ({
  dataApi,
  data: propData,
  contextId,
  tableName,
  displayTitle,
  displayName,
  heading,
  filterModule,
  datagridKey: datagridKeyProp,
  defaultColumns: defaultColumnsProp,
  orgId: propOrgId,
  organizationUserId,
  userData: propUserData,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const token = localStorage.getItem("token");
  const { userData: storeUserData } = useAuthStore();

  // Resolved list title for banner / intro (prop → route state → session → tableName)
  const listHeading = useMemo(() => {
    const fromProp = displayTitle || displayName || heading;
    if (fromProp) return String(fromProp).trim();

    const fromState =
      location.state?.displayName ||
      location.state?.heading ||
      location.state?.displayTitle;
    if (fromState) return String(fromState).trim();

    if (tableName) {
      try {
        const stored = sessionStorage.getItem(
          `personalize_heading_${tableName}`
        );
        if (stored) return stored.trim();
      } catch (_) {
        /* ignore */
      }
    }

    return humanizeTableName(tableName) || "List";
  }, [displayTitle, displayName, heading, location.state, tableName]);
  
  // Use prop userData if provided, otherwise fall back to store
  const userData = propUserData || storeUserData;
  
  // Use prop orgId if provided, otherwise derive from userData
  const orgId = propOrgId || userData?.organization?.organization_id;

  // Consolidated state
  const [columns, setColumns] = useState([]);
  const [defaultColumns, setDefaultColumns] = useState([]); // Single source of truth for defaults
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isLoadingConfig, setIsLoadingConfig] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [autoSaveState, setAutoSaveState] = useState("idle");
  const [configSource, setConfigSource] = useState(null);
  const [filters, setFilters] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [filtersLoaded, setFiltersLoaded] = useState(false);

  // Refs
  const saveTimeout = useRef(null);
  const isInitialLoad = useRef(true);
  const skipAutosaveRef = useRef(false);
  const suppressAutosaveUntilEditRef = useRef(false);
  const layoutInitializedRef = useRef(false);
  const isMountedRef = useRef(true);
  const orgEntityId = userData?.organization_entity_id || null;

  const resolveDatagridKey = useCallback(() => {
    return buildDatagridKey(datagridKeyProp || tableName, orgId);
  }, [datagridKeyProp, tableName, orgId]);

  // Row identity columns must never appear in personalize UI
  const isIdentityColumn = useCallback((colOrKey) => {
    const key = String(
      typeof colOrKey === "string"
        ? colOrKey
        : colOrKey?.key || colOrKey?.field || ""
    )
      .toLowerCase()
      .trim();
    const label = String(
      typeof colOrKey === "string" ? "" : colOrKey?.label || ""
    )
      .toLowerCase()
      .trim();

    // Business fields that look like IDs but must stay in Personalize
    const allowlistedKeys = new Set(["upi_id"]);
    if (allowlistedKeys.has(key)) return false;

    if (key === "id" || key.endsWith("_id")) return true;
    if (label === "id" || label.endsWith(" id")) return true;
    return false;
  }, []);

  // Map columns with metadata
  const mapColumnsWithMeta = useCallback(
    (cols = []) =>
      cols
        .filter((col) => !isIdentityColumn(col))
        .map((col, index) =>
          withReadableColumnWidth({
            ...col,
            key: col.key || col.field,
            required: col.mandatory || col.required || false,
            order: col.order ?? index,
            pinned: col.pinned === "left" ? "left" : undefined,
          })
        ),
    [isIdentityColumn]
  );

  // Generate columns from data (soft fallback only — general config is catalog authority)
  const generateColumnsFromData = useCallback(
    (dataArray) => {
      if (!dataArray?.length) return [];

      const sampleRow = dataArray[0];
      const keys = Object.keys(sampleRow).filter(
        (key) => !isIdentityColumn(key)
      );

      return keys.map((key, index) =>
        withReadableColumnWidth({
          key,
          label: key
            .split("_")
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(" "),
          visible: index < 5,
          pinned: undefined,
          width: 150,
          required: index === 1,
        })
      );
    },
    [isIdentityColumn]
  );

  // Load layout from backend (org → general; optional prefer_general)
  const loadLayoutFromBackend = useCallback(
    async ({ preferGeneral = false } = {}) => {
      const datagridKey = resolveDatagridKey();
      if (!orgId || !datagridKey) {
        console.warn("Missing orgId or datagridKey/tableName");
        setIsLoadingConfig(false);
        return null;
      }

      try {
        const requestPayload = {
          datagrid_key: datagridKey,
          organization_id: orgId,
        };

        if (preferGeneral) {
          requestPayload.prefer_general = true;
        } else if (organizationUserId) {
          requestPayload.organization_user_id = organizationUserId;
        }

        const response = await fetch(`${MAIN_URL}/api/datagrid/get-by-context`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(token && { Authorization: `Bearer ${token}` }),
          },
          body: JSON.stringify(requestPayload),
        });

        if (response.ok) {
          const responseData = await response.json();

          if (responseData.datagrid?.datagrid_default_configuration) {
            const config = responseData.datagrid.datagrid_default_configuration;
            setConfigSource(responseData.type);
            console.log(
              "✓ Loaded backend config:",
              config.columns?.length,
              "columns",
              responseData.type
            );
            return { config, type: responseData.type };
          }
        } else if (response.status === 404) {
          console.log("No saved layout found, using defaults");
        }

        setConfigSource(null);
        return null;
      } catch (error) {
        console.error("Error loading layout:", error);
        setConfigSource(null);
        return null;
      } finally {
        setIsLoadingConfig(false);
      }
    },
    [orgId, resolveDatagridKey, token, organizationUserId]
  );

  // Build layout data for saving
  const buildLayoutData = useCallback(
    (columnSnapshot, filtersSnapshot = []) => ({
      columns: columnSnapshot.map((col, index) => ({
        key: col.key,
        label: col.label,
        width: col.width,
        visible: col.visible,
        pinned: col.pinned || undefined,
        order: index,
        required: col.required,
      })),
      filters: filtersSnapshot || [],
      timestamp: new Date().toISOString(),
    }),
    []
  );

  // Save layout to backend (organization override; defaults come from general config)
  const saveLayoutToBackend = useCallback(
    async (layoutData) => {
      if (!orgId || !isMountedRef.current) {
        console.error("Cannot save: missing orgId or unmounted");
        return false;
      }

      const datagridKey = resolveDatagridKey();
      if (!datagridKey) {
        console.error("Cannot save: invalid datagrid key");
        return false;
      }

      const payload = {
        organization_id: orgId,
        organization_entity_id: orgEntityId,
        datagrid_key: datagridKey,
        datagrid_default_configuration: layoutData,
      };

      try {
        const response = await fetch(`${MAIN_URL}/api/organization-datagrids`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(token && { Authorization: `Bearer ${token}` }),
          },
          body: JSON.stringify(payload),
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.message || `Save failed: ${response.status}`);
        }

        console.log("✓ Layout saved");
        if (isMountedRef.current) {
          setAutoSaveState("saved");
          setConfigSource("organization");
        }

        return true;
      } catch (error) {
        console.error("Save error:", error);
        setAutoSaveState("error");
        throw error;
      }
    },
    [orgId, orgEntityId, resolveDatagridKey, token]
  );

  // Reset: delete org override, then reload get-by-context (falls through to general)
  const resetLayoutToDefault = useCallback(async () => {
    if (!orgId) {
      console.error("Cannot reset: missing orgId");
      return;
    }

    const datagridKey = resolveDatagridKey();
    if (!datagridKey) {
      console.error("Cannot reset: invalid datagrid key");
      return;
    }

    try {
      skipAutosaveRef.current = true;
      isInitialLoad.current = true;

      const params = new URLSearchParams({
        datagrid_key: datagridKey,
        organization_id: orgId.toString(),
      });

      if (organizationUserId && configSource === "organization_user") {
        params.append("organization_user_id", organizationUserId.toString());
      }

      const response = await fetch(
        `${MAIN_URL}/api/datagrid/get-by-context?${params.toString()}`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            ...(token && { Authorization: `Bearer ${token}` }),
          },
        }
      );

      if (!response.ok) {
        throw new Error(`Reset failed: ${response.status}`);
      }

      console.log("✓ Layout reset; loading remaining backend defaults");

      // Prefer remaining backend defaults (general/org) merged onto catalog
      const result = await loadLayoutFromBackend();
      const catalog = defaultColumns;

      if (result?.config?.columns?.length > 0) {
        setColumns(
          mapColumnsWithMeta(
            mergeColumnLayouts(catalog, result.config.columns)
          )
        );
        setFilters(
          Array.isArray(result.config.filters) ? result.config.filters : []
        );
      } else {
        setColumns(mapColumnsWithMeta(catalog));
        setFilters([]);
        setConfigSource(null);
      }

      setAutoSaveState("saved");
      isInitialLoad.current = true;
      skipAutosaveRef.current = true;
      suppressAutosaveUntilEditRef.current = true;

      return true;
    } catch (error) {
      console.error("Reset error:", error);
      setAutoSaveState("error");
      skipAutosaveRef.current = false;
      throw error;
    }
  }, [
    orgId,
    token,
    resolveDatagridKey,
    organizationUserId,
    configSource,
    loadLayoutFromBackend,
    defaultColumns,
    mapColumnsWithMeta,
  ]);

  // Cleanup on unmount
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (saveTimeout.current) {
        clearTimeout(saveTimeout.current);
      }
    };
  }, []);

  const applyDefaultColumns = useCallback(
    async (rows) => {
      // Soft fallback from API rows / prop columns — general config wins when present.
      const fromData =
        rows?.length > 0 ? generateColumnsFromData(rows) : [];
      const fromProp = Array.isArray(defaultColumnsProp)
        ? defaultColumnsProp.map((col, index) => ({
            ...col,
            key: col.key || col.field,
            order: col.order ?? index,
          }))
        : [];

      const byKey = new Map();
      [...fromData, ...fromProp].forEach((col, index) => {
        const key = col.key || col.field;
        if (!key || byKey.has(key)) return;
        byKey.set(key, {
          ...col,
          key,
          order: col.order ?? index,
        });
      });
      const softFallbackColumns = Array.from(byKey.values());

      const catalogColumns = await resolvePersonalizeCatalogColumns({
        datagridKeyBase: datagridKeyProp || tableName,
        orgId,
        softFallbackColumns,
        sampleRow: rows?.[0] || null,
      });

      if (catalogColumns.length > 0) {
        setDefaultColumns(mapColumnsWithMeta(catalogColumns));
      } else if (softFallbackColumns.length > 0) {
        setDefaultColumns(mapColumnsWithMeta(softFallbackColumns));
      }
    },
    [
      defaultColumnsProp,
      generateColumnsFromData,
      mapColumnsWithMeta,
      datagridKeyProp,
      tableName,
      orgId,
    ]
  );

  // Fetch data from API or use parent-provided rows (mapped for display keys)
  useEffect(() => {
    const fetchData = async () => {
      if (propData !== undefined && propData !== null) {
        const rows = Array.isArray(propData) ? propData : [];
        setData(rows);
        await applyDefaultColumns(rows);
        return;
      }

      if (defaultColumnsProp?.length) {
        await applyDefaultColumns([]);
      }

      const apiUrl = dataApi;

      // Fetch from API when dataApi is provided
      if (apiUrl) {
        setLoading(true);
        setError(null);

        try {
          console.log("Fetching from API:", apiUrl);
          const response = await axios.get(apiUrl, {
            params: contextId ? { context: contextId } : {},
            headers: getApiHeaders(),
          });

          // Check if API returned an error
          if (response.data.success === false) {
            console.warn("API returned success: false", response.data);
            // Still try to extract data if available, even if success is false
            if (response.data.data && Array.isArray(response.data.data)) {
              setData(response.data.data);
              await applyDefaultColumns(response.data.data);
            } else {
              // No data available, but still allow personalization with defaults/prop columns
              setData([]);
              await applyDefaultColumns([]);
            }
            return;
          }

          // Extract data array from any common API response shape (no endpoint-specific logic)
          const fetchedData = extractDataArrayFromResponse(response.data);

          console.log("✓ Fetched:", fetchedData.length, "records", "Response:", response.data);
          setData(fetchedData);
          await applyDefaultColumns(fetchedData);
        } catch (err) {
          setError(err.message || "Failed to fetch data");
          console.error("Fetch error:", err);
          // Keep prop-defined columns available even when fetch fails
          await applyDefaultColumns([]);
        } finally {
          setLoading(false);
        }
      }
    };

    fetchData();
  }, [
    dataApi,
    propData,
    contextId,
    token,
    orgId,
    tableName,
    defaultColumnsProp,
    applyDefaultColumns,
  ]);

  // Initialize layout once from backend; later catalog updates only merge new fields
  useEffect(() => {
    let cancelled = false;

    const initializeLayout = async () => {
      const hasParentCatalog = Array.isArray(defaultColumnsProp);
      const parentCatalog = hasParentCatalog
        ? mapColumnsWithMeta(
            defaultColumnsProp.map((col, index) => ({
              ...col,
              key: col.key || col.field,
              order: col.order ?? index,
            }))
          )
        : null;

      const catalog = hasParentCatalog ? parentCatalog : defaultColumns;

      // Wait for catalog from general / API discovery (avoid merging prefs onto empty)
      if (
        !layoutInitializedRef.current &&
        !hasParentCatalog &&
        catalog.length === 0 &&
        (propData !== undefined || dataApi)
      ) {
        return;
      }

      // After first init: re-merge catalog universe with current prefs (CRM-style)
      if (layoutInitializedRef.current) {
        if (catalog.length === 0) return;
        skipAutosaveRef.current = true;
        setColumns((prev) => {
          if (!prev?.length) {
            return mapColumnsWithMeta(catalog);
          }
          return mapColumnsWithMeta(mergeColumnLayouts(catalog, prev));
        });
        return;
      }

      console.log("Initializing layout...");
      isInitialLoad.current = true;
      const result = await loadLayoutFromBackend();
      if (cancelled) return;

      if (result?.config?.columns?.length > 0) {
        console.log("Merging backend config onto catalog:", result.type);
        setColumns(
          mapColumnsWithMeta(
            mergeColumnLayouts(catalog, result.config.columns)
          )
        );

        if (
          result.config.filters &&
          Array.isArray(result.config.filters) &&
          result.config.filters.length > 0
        ) {
          setFilters(result.config.filters);
        }
      } else if (catalog.length > 0) {
        console.log("No backend config; using catalog columns");
        setColumns(mapColumnsWithMeta(catalog));
      }

      layoutInitializedRef.current = true;
      setIsLoadingConfig(false);
      setFiltersLoaded(true);
      isInitialLoad.current = true;
    };

    initializeLayout();
    return () => {
      cancelled = true;
    };
  }, [
    defaultColumns,
    defaultColumnsProp,
    dataApi,
    propData,
    loadLayoutFromBackend,
    mapColumnsWithMeta,
    tableName,
  ]);

  // Auto-save when columns change (debounced) — skip after reset / initial load
  useEffect(() => {
    if (columns.length === 0 || isLoadingConfig) {
      return;
    }

    if (
      suppressAutosaveUntilEditRef.current ||
      skipAutosaveRef.current ||
      isInitialLoad.current
    ) {
      skipAutosaveRef.current = false;
      isInitialLoad.current = false;
      return;
    }

    if (saveTimeout.current) {
      clearTimeout(saveTimeout.current);
    }

    saveTimeout.current = setTimeout(() => {
      if (!isMountedRef.current) return;

      const layoutData = buildLayoutData(columns, filters);
      console.log("Auto-saving...");
      setAutoSaveState("saving");

      saveLayoutToBackend(layoutData).catch((error) => {
        console.error("Auto-save failed:", error);
        setAutoSaveState("error");
      });
    }, 700);

    return () => {
      if (saveTimeout.current) {
        clearTimeout(saveTimeout.current);
      }
    };
  }, [columns, filters, isLoadingConfig, saveLayoutToBackend, buildLayoutData]);

  // Drop any leftover id columns from older saved layouts
  useEffect(() => {
    if (!columns.some(isIdentityColumn)) return;
    setColumns((prev) => prev.filter((col) => !isIdentityColumn(col)));
  }, [columns, isIdentityColumn]);

  // Computed values — always exclude identity columns from the UI lists
  const displayColumns = useMemo(
    () => columns.filter((col) => !isIdentityColumn(col)),
    [columns, isIdentityColumn]
  );

  const filteredColumns = useMemo(() => {
    if (!searchQuery.trim()) return displayColumns;
    const query = searchQuery.toLowerCase();
    return displayColumns.filter(
      (col) =>
        col.label?.toLowerCase().includes(query) ||
        col.key?.toLowerCase().includes(query)
    );
  }, [displayColumns, searchQuery]);

  const visibleColumns = useMemo(
    () => displayColumns.filter((col) => col.visible),
    [displayColumns]
  );

  const hiddenColumns = useMemo(
    () => displayColumns.filter((col) => !col.visible),
    [displayColumns]
  );

  const allNonMandatoryVisible = useMemo(() => {
    const nonMandatory = displayColumns.filter((col) => !col.required);
    return nonMandatory.length > 0 && nonMandatory.every((col) => col.visible);
  }, [displayColumns]);

  const noNonMandatoryVisible = useMemo(() => {
    const nonMandatory = displayColumns.filter((col) => !col.required);
    return nonMandatory.length > 0 && nonMandatory.every((col) => !col.visible);
  }, [displayColumns]);

  // Event handlers — clear post-reset autosave suppress on real user edits
  const markUserEdit = useCallback(() => {
    suppressAutosaveUntilEditRef.current = false;
  }, []);

  const toggleColumnVisibility = useCallback(
    (key) => {
      markUserEdit();
      setColumns((prev) =>
        prev.map((col) => {
          if (col.key !== key || col.required) return col;
          const nextVisible = !col.visible;
          if (!nextVisible) {
            return { ...col, visible: false };
          }
          return withReadableColumnWidth({ ...col, visible: true });
        })
      );
    },
    [markUserEdit]
  );

  const toggleColumnPin = useCallback(
    (key) => {
      markUserEdit();
      setColumns((prev) =>
        prev.map((col) =>
          col.key === key
            ? { ...col, pinned: col.pinned === "left" ? undefined : "left" }
            : col
        )
      );
    },
    [markUserEdit]
  );

  const handleDragStart = useCallback(
    (e, index) => {
      markUserEdit();
      setDraggedIndex(index);
      e.dataTransfer.effectAllowed = "move";
    },
    [markUserEdit]
  );

  const handleDragOver = useCallback((e, index) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;

    markUserEdit();
    setColumns((prev) => {
      const newColumns = [...prev];
      const draggedColumn = newColumns[draggedIndex];
      newColumns.splice(draggedIndex, 1);
      newColumns.splice(index, 0, draggedColumn);
      return newColumns;
    });
    setDraggedIndex(index);
  }, [draggedIndex, markUserEdit]);

  const handleDragEnd = useCallback(() => {
    setDraggedIndex(null);
  }, []);

  const handleSelectAll = useCallback((event) => {
    markUserEdit();
    const isChecked = event.target.checked;
    setColumns((prev) =>
      prev.map((col) => {
        const nextCol = {
          ...col,
          visible: col.required ? true : isChecked,
        };
        return nextCol.visible ? withReadableColumnWidth(nextCol) : nextCol;
      })
    );
  }, [markUserEdit]);

  // Filter handlers
  const handleFiltersChange = useCallback((newFilters) => {
    markUserEdit();
    setFilters(newFilters);
  }, [markUserEdit]);

  const handleApplyFilters = useCallback((appliedFilters) => {
    markUserEdit();
    setFilters(appliedFilters);
    console.log("Filters applied:", appliedFilters);
  }, [markUserEdit]);

  const handleClearFilters = useCallback(() => {
    markUserEdit();
    setFilters([]);
    setFilteredData(data);
  }, [data, markUserEdit]);

  // Loading states
  if (loading || isLoadingConfig) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  // Only show "No data available" when there is no data and no columns (e.g. no saved layout)
  if ((!Array.isArray(data) || data.length === 0) && columns.length === 0) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">No data available. Please ensure there are records or a saved layout configuration.</Alert>
      </Box>
    );
  }

  const allVisible = allNonMandatoryVisible;
  const noneVisible = noNonMandatoryVisible;
  const toggleAll = () => handleSelectAll({ target: { checked: !allVisible } });

  return (
    <div className="ems-fade-up">
      <p className="ems-pv-intro">
        <b>{listHeading}</b> — personalize columns, order, width, and
        visibility. Changes save automatically for your organization.
      </p>

      {/* Mode banner */}
      <div className="ems-card ems-pv-mode">
        <div>
          <div className="ems-pv-mode-title">
            {listHeading ? (
              <span className="ems-pv-mode-tag">
                {listHeading}
              </span>
            ) : null}
            Personalization Mode
          </div>
          <div className="ems-pv-mode-sub">
            Starts from general defaults. Changes are saved for your organization automatically.
          </div>
        </div>
        <button
          className="ems-btn ems-danger"
          onClick={async () => {
            // Force-persist org layout before leaving so list/org config stay in sync
            if (
              columns.length > 0 &&
              !suppressAutosaveUntilEditRef.current &&
              orgId
            ) {
              try {
                setAutoSaveState("saving");
                await saveLayoutToBackend(buildLayoutData(columns, filters));
              } catch (e) {
                console.error("Exit save failed:", e);
              }
            }
            navigate(-1);
          }}
        >
          <X size={16} />
          Exit Personalization
        </button>
      </div>

      {/* Fields / Columns */}
      <div className="ems-card ems-pv-fields">
        <h2 className="ems-pv-h">Fields / Columns</h2>

        <div className="ems-pv-search">
          <Search size={17} />
          <input
            placeholder="Search columns..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          {searchQuery && (
            <button
              className="ems-pv-search-clear"
              onClick={() => setSearchQuery("")}
              aria-label="Clear"
            >
              <X size={15} />
            </button>
          )}
        </div>

        <button className="ems-pv-selectall" type="button" onClick={toggleAll}>
          <span
            className={
              "ems-pv-check" +
              (allVisible ? " on" : !noneVisible ? " mixed" : "")
            }
          >
            {allVisible ? (
              <Check size={13} strokeWidth={3} />
            ) : (
              !noneVisible && <span className="ems-pv-dash" />
            )}
          </span>
          <span>Select All</span>
        </button>

        <div className="ems-pv-cols">
          {/* Visible */}
          <div>
            <div className="ems-pv-col-head visible">
              <Eye size={14} />
              Visible Columns
              <span className="ems-pv-col-n">{visibleColumns.length}</span>
            </div>
            <div className="ems-pv-list">
              {filteredColumns
                .filter((col) => col.visible)
                .map((col) => {
                  const index = columns.findIndex((c) => c.key === col.key);
                  return (
                    <div
                      key={col.key}
                      className="ems-pv-item"
                      draggable
                      onDragStart={(e) => handleDragStart(e, index)}
                      onDragOver={(e) => handleDragOver(e, index)}
                      onDragEnd={handleDragEnd}
                    >
                      <span className="ems-pv-grip">
                        <GripVertical size={16} />
                      </span>
                      <span
                        className={"ems-pv-check" + (col.visible ? " on" : "")}
                        onClick={() =>
                          !col.required && toggleColumnVisibility(col.key)
                        }
                        style={col.required ? { opacity: 0.5, cursor: "default" } : undefined}
                      >
                        {col.visible && <Check size={13} strokeWidth={3} />}
                      </span>
                      <span className="ems-pv-item-label">
                        {col.label}
                        {col.required && (
                          <span className="ems-pv-required">Required</span>
                        )}
                      </span>
                      <button
                        className={"ems-pv-act" + (col.pinned === "left" ? " on" : "")}
                        title={col.pinned === "left" ? "Unpin column" : "Pin column"}
                        onClick={() => toggleColumnPin(col.key)}
                      >
                        {col.pinned === "left" ? <Pin size={15} /> : <PinOff size={15} />}
                      </button>
                      <button
                        className="ems-pv-act"
                        title="Hide column"
                        disabled={col.required}
                        onClick={() => !col.required && toggleColumnVisibility(col.key)}
                      >
                        <EyeOff size={16} />
                      </button>
                    </div>
                  );
                })}
              {filteredColumns.filter((col) => col.visible).length === 0 && (
                <div className="ems-pv-empty">
                  {searchQuery ? "No visible columns match." : "No visible columns."}
                </div>
              )}
            </div>
          </div>

          {/* Hidden */}
          <div>
            <div className="ems-pv-col-head hidden">
              <EyeOff size={14} />
              Hidden Columns
              <span className="ems-pv-col-n">{hiddenColumns.length}</span>
            </div>
            <div className="ems-pv-list">
              {filteredColumns
                .filter((col) => !col.visible)
                .map((col) => (
                  <div
                    key={col.key}
                    className="ems-pv-item ghost"
                    onClick={() => toggleColumnVisibility(col.key)}
                  >
                    <span className="ems-pv-check" />
                    <span className="ems-pv-item-label">{col.label}</span>
                    <button
                      className="ems-pv-act"
                      title="Show column"
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleColumnVisibility(col.key);
                      }}
                    >
                      <EyeOff size={16} />
                    </button>
                  </div>
                ))}
              {filteredColumns.filter((col) => !col.visible).length === 0 && (
                <div className="ems-pv-empty">
                  {searchQuery ? "No hidden columns match." : "All columns are visible."}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Actions bar */}
      <div className="ems-card ems-pv-actions">
        <button className="ems-btn" onClick={resetLayoutToDefault}>
          <RefreshCw size={16} />
          Reset to Default
        </button>
        <div className="ems-pv-spacer" />
        <span
          className={
            "ems-pv-saved" +
            (autoSaveState === "saving" ? " saving" : "") +
            (autoSaveState === "error" ? " error" : "")
          }
        >
          {autoSaveState === "saving" ? (
            <>
              <span className="ems-pv-spin" />
              Saving changes…
            </>
          ) : autoSaveState === "error" ? (
            "Save failed"
          ) : autoSaveState === "saved" ? (
            <>
              <Check size={15} strokeWidth={2.6} />
              All changes saved
            </>
          ) : (
            ""
          )}
        </span>
      </div>

      {/* Preview */}
      <div className="ems-card ems-pv-preview">
        <div className="ems-pv-preview-head">
          <h2 className="ems-pv-h">Preview</h2>
          <span className="ems-pv-preview-note">
            {visibleColumns.length} columns · live preview of your grid
          </span>
        </div>
        <div className="ems-pv-table-wrap">
          <table className="ems-pv-table">
            <thead>
              <tr>
                {visibleColumns.map((col) => (
                  <th
                    key={col.key}
                    style={{
                      position: col.pinned === "left" ? "sticky" : "static",
                      left: col.pinned === "left" ? 0 : undefined,
                      zIndex: col.pinned === "left" ? 2 : 1,
                      background: "var(--ems-surface-2)",
                      minWidth: col.width || estimateColumnWidth(col.label),
                      width: col.width || estimateColumnWidth(col.label),
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                      {col.label}
                      {col.pinned === "left" && <Pin size={12} />}
                    </Box>
                  </th>
                ))}
                {visibleColumns.length === 0 && (
                  <th className="ems-pv-th-empty">No columns selected</th>
                )}
              </tr>
            </thead>
            <tbody>
              {Array.isArray(data) &&
                data.slice(0, 10).map((row, rowIdx) => (
                  <tr key={rowIdx}>
                    {visibleColumns.map((col) => (
                      <td
                        key={col.key}
                        style={{
                          position: col.pinned === "left" ? "sticky" : "static",
                          left: col.pinned === "left" ? 0 : undefined,
                          zIndex: col.pinned === "left" ? 2 : 1,
                          background: col.pinned === "left" ? "var(--ems-surface)" : "transparent",
                          minWidth: col.width || estimateColumnWidth(col.label),
                          width: col.width || estimateColumnWidth(col.label),
                        }}
                      >
                        {formatPreviewCellValue(row?.[col.key])}
                      </td>
                    ))}
                    {visibleColumns.length === 0 && (
                      <td className="ems-pv-td-empty">Enable columns above to preview the grid.</td>
                    )}
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default PersonalizeDataGrid;
                         
